import { NO_VIDEO_GREY_BG } from '@anm/constants/colors';
import hex2rgba from '@anm/helpers/color/hex2rgba';
import getFromCdn from '@anm/helpers/getFromCdn';
import isMobileOrIPad from '@anm/helpers/is/isMobileOrIPad';
import { media } from '@anm/styles/helpers';
import styled, { css } from 'styled-components';

import { ContextVariant } from '.';

type Vertical = {
  isVertical: boolean;
};

type PlaceholderProps = {
  width: number;
  height: number;
  showContextOnMobiles: boolean;
} & Vertical;

type ThumbWrapperProps = {
  variant: ContextVariant;
  showContextOnMobiles: boolean;
};

const contextVariants: { [key in ContextVariant]: ReturnType<typeof css> } = {
  email: css`
    &:before {
      background-image: url(${getFromCdn('images/email_graphics_top.png')});
      padding-top: 27.31%;
    }

    &:after {
      background-image: url(${getFromCdn('images/email_graphics_bottom.png')});
      padding-top: 33.46%;
    }
  `,
  inline: css`
    &:before {
      background-image: url(${getFromCdn('images/inline_graphics_top.svg')});
      padding-top: 16.8%;
    }

    &:after {
      background-image: url(${getFromCdn('images/inline_graphics_bottom.svg')});
      padding-top: 26.5%;
    }
  `,
  highlight: css`
    &:before {
      background-image: url(${getFromCdn('images/highlight_graphics_top.svg')});
      padding-top: 35.4%;
    }

    &:after {
      background-image: url(${getFromCdn('images/highlight_graphics_bottom.svg')});
      padding-top: 26.46%;
    }
  `,
  thumbnail: css`
    &:before {
      background-image: url(${getFromCdn('images/inline_graphics_top.svg')});
      padding-top: 16.8%;
    }

    &:after {
      background-image: url(${getFromCdn('images/inline_graphics_bottom.svg')});
      padding-top: 26.5%;
    }
  `,
  text: css`
    &:before {
      background-image: url(${getFromCdn('images/text_graphics_top.svg')});
      padding-top: 50.8%;
    }

    &:after {
      background-image: url(${getFromCdn('images/text_graphics_bottom.svg')});
      padding-top: 11.3%;
    }
  `
};

const playIconOnMobiles = media.tablet`
  width: 50px;
  height: 50px;
  background-size: 30%;
`;

const thumbnailOnMobiles = media.tablet`
  padding: 0;
`;

const placeholderOnMobiles = media.tablet`
  margin: 0 auto 20px;
`;

const thumbWrapperOnMobiles = media.tablet`
  max-width: 297px;
  margin: 0 auto 20px;
`;

export const ThumbWrapper = styled.div<ThumbWrapperProps>`
  width: 80%;
  max-width: 520px;
  margin: 0 auto 24px;
  position: relative;
  overflow: hidden;

  &:before,
  &:after {
    content: '';
    display: ${({ showContextOnMobiles }) => (!showContextOnMobiles && isMobileOrIPad() ? 'none' : 'block')};
    background-repeat: no-repeat;
    background-size: cover;
    height: 0;
  }

  ${thumbWrapperOnMobiles};

  ${({ variant }) => contextVariants[variant]};
`;

const verticalStyles = css`
  max-height: calc(520px);
`;

export const Thumbnail = styled.img<Vertical>`
  display: block;
  padding: 40px;
  max-width: 100%;
  margin: 0 auto;
  ${({ isVertical }) => isVertical && verticalStyles}

  ${thumbnailOnMobiles};
`;

export const PlayIcon = styled.div<{ color: string }>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 70px;
  height: 70px;
  transform: translate(-50%, calc(-50% - 10px));
  border-radius: 50%;
  background: url(${getFromCdn('images/white_play_icon.svg')}) no-repeat 55% center,
    ${({ color }) => hex2rgba(color, 0.75)};

  ${playIconOnMobiles};
`;

export const Placeholder = styled.div<PlaceholderProps>`
  width: ${({ width }) => `${width}px`};
  height: ${({ height }) => `${height}px`};
  max-width: 100%;
  margin: 40px auto;
  background: url(${getFromCdn('images/no_video_icon.svg')}) no-repeat center / 20%, ${NO_VIDEO_GREY_BG};
  ${({ isVertical }) => isVertical && verticalStyles};
  ${({ showContextOnMobiles }) => !showContextOnMobiles && placeholderOnMobiles};
`;
